<template>
  <div class="page-header" :class="{ 'locale-zh-CN': isZhLocale }">
    <!-- Top navigation bar -->
    <div class="top-navbar">
      <div class="navbar-content">
        <div class="logo-area">
          <img :src="safeLogo" alt="logo" class="logo" @error="onImgError" />
          <span class="site-title">{{ config.siteInfo.title }}</span>
        </div>
        <div class="right-cluster">
          <el-dropdown class="lang-entry" trigger="click" @command="handleLocaleCommand">
            <span class="el-dropdown-link">
              {{ currentLocaleLabel }}
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="zh-CN">中文</el-dropdown-item>
              <el-dropdown-item command="en-US">English</el-dropdown-item>
              <el-dropdown-item command="ru-RU">Русский</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <div class="auth-area">
          <template v-if="isLoggedIn">
            <span
              class="auth-link nick"
              :title="userInfo.nickName || userInfo.userId || pageHeaderText.userFallback"
              @click="handleUserCenter"
            >
              <img v-if="userInfo.avatar" :src="safeAvatar" alt="avatar" class="avatar" @error="onImgError" />
              {{ userInfo.nickName || userInfo.userId || pageHeaderText.userFallback }}
            </span>
            <span class="divider">|</span>
            <span class="auth-link" @click="handleLogout">{{ config.siteInfo.logoutText || pageHeaderText.logout }}</span>
          </template>
          <template v-else>
            <span class="auth-link" @click="handleLogin">{{ pageHeaderText.login }}</span>
            <span class="divider">|</span>
            <span class="auth-link" @click="handleRegister">{{ pageHeaderText.register }}</span>
          </template>
          </div>
        </div>
      </div>
    </div>

    <div class="line"></div>

    <!-- Functional modules -->
    <ModuleList :modules="config.modules" @module-click="handleModuleClick" />
  </div>
</template>

<script>
import ModuleList from '@/components/ModuleList.vue'
import request from '@/utils/request'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import commonApi from '@/api/common'
import { getText, getCurrentLocale, setPortalLocale } from '@/utils/i18nText'
import { setBehaviorUserId, clearBehaviorUserId } from '@/utils/behaviorTrack'

export default {
  name: 'PageHeader',
  components: {
    ModuleList
  },
  props: {
    // Configuration
    config: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      isLoggedIn: false,
      userInfo: {
        nickName: '',
        userId: '',
        avatar: ''
      },
      loadingUser: false
    }
  },
  computed: {
    currentLocaleLabel() {
      const map = {
        'zh-CN': '中文',
        'en-US': 'English',
        'ru-RU': 'Русский'
      }
      return map[getCurrentLocale()] || '中文'
    },
    pageHeaderText() {
      return {
        userFallback: getText('pageHeader.userFallback'),
        login: getText('pageHeader.login'),
        register: getText('pageHeader.register'),
        logout: getText('pageHeader.logout'),
        userInfoLoadFailed: getText('pageHeader.userInfoLoadFailed')
      }
    },
    safeLogo() {
      return getSafeImage(this.config.siteInfo?.logo)
    },
    safeAvatar() {
      return getSafeImage(this.userInfo.avatar)
    }
  },
  mounted() {
    this.fetchUserInfo()
  },
  methods: {
    handleLocaleCommand(locale) {
      if (!locale || locale === getCurrentLocale()) return
      setPortalLocale(locale)
    },
    // Handle functional module click
    handleModuleClick(module) {
      this.$emit('module-click', module)
    },

    async fetchUserInfo() {
      this.loadingUser = true
      try {
        const res = await request({
          url: '/uoc/umc/user/my/q/simpleinfo',
          method: 'get'
        })
        const result = res.data || {}
        if (result.status === 0 && result.data) {
          const data = result.data
          this.isLoggedIn = true
          this.userInfo = {
            nickName: data.nickName || data.userId || '',
            userId: data.userId || '',
            avatar: data.avatar
              ? (String(data.avatar).startsWith('http') ? data.avatar : data.avatar)
              : ''
          }
          if (data.userId) setBehaviorUserId(data.userId)
        } else {
          this.isLoggedIn = false
          this.userInfo = { nickName: '', userId: '', avatar: '' }
          clearBehaviorUserId()
        }
      } catch (error) {
        console.error(`${this.pageHeaderText.userInfoLoadFailed}:`, error)
        this.isLoggedIn = false
        this.userInfo = { nickName: '', userId: '', avatar: '' }
        clearBehaviorUserId()
      } finally {
        this.loadingUser = false
      }
    },

    handleLogin() {
      const currentUrl = encodeURIComponent(window.location.href)
      window.open(`/main/#/login?redirectURL=${currentUrl}`, '_blank')
      // Listen for window focus; refresh user info when returning.
      this.setupAuthCheck()
    },

    handleRegister() {
      window.open('/main/#/register', '_blank')
      // Listen for window focus; refresh user info when returning.
      this.setupAuthCheck()
    },

    handleUserCenter() {
      try {
        window.location.assign('/vportal-default/#/home')
      } catch (e) {
        window.location.href = '/vportal-default/#/home'
      }
    },

    setupAuthCheck() {
      // When window regains focus, check whether user is logged in.
      const checkUser = () => {
        this.fetchUserInfo()
      }
      window.addEventListener('focus', checkUser, { once: true })
    },

    async handleLogout() {
      // Logout: align with the login module implementation (/j_spring_security_logout)
      try {
        await commonApi.logoutApi()
      } catch (e) {
        // ignore (best effort to clean front-end state even if API fails)
      } finally {
        this.isLoggedIn = false
        this.userInfo = { nickName: '', userId: '', avatar: '' }
        clearBehaviorUserId()
        this.$emit('logout')
      }
    },

    onImgError(event) {
      handleImgError(event)
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  width: 1920px;
  background: #FFFFFF;
  box-shadow: 0px 8px 12px 0px rgba(186, 186, 172, 0.2);
  border-radius: 20px;
}

// Top navigation bar
.top-navbar {
  height: 60px;
  position: relative;
  z-index: 100;

  .navbar-content {
    max-width: 1920px;
    height: 100%;
    margin: 0 auto;
    // padding: 0 80px;
    padding-left: 17px;
    padding-right: 37px;
    padding-top: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .logo-area {
      display: flex;
      align-items: center;

      .logo {
        height: 32px;
        margin-right: 12px;
      }

      .site-title {
        font-size: 24px;
        color: #333;
        font-weight: bold;
        letter-spacing: 0.5px;
      }
    }

    .right-cluster {
      display: flex;
      align-items: center;
      gap: 20px;
    }

    .lang-entry {
      .el-dropdown-link {
        display: inline-flex;
        align-items: center;
        color: #666;
        cursor: pointer;
        font-size: 14px;
        padding: 6px 12px;
        border-radius: 4px;
        border: 1px solid #ddd;
        background: #fafafa;
      }

      .el-dropdown-link:hover {
        border-color: #b8945f;
        color: #a37f4d;
      }
    }

    .auth-area {
      display: flex;
      align-items: center;
      gap: 8px;

      .auth-link {
        cursor: pointer;
        font-size: 14px;
        color: #666;
        padding: 6px 16px;
        border-radius: 4px;
        transition: all 0.3s;

        &.nick {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .avatar {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          object-fit: cover;
        }

        &:first-child {
          color: #333;
          font-weight: 500;
        }

        &:last-child {
          color: #b8945f;

          &:hover {
            background: rgba(184, 148, 95, 0.08);
            color: #a37f4d;
          }
        }
      }

      .divider {
        color: #ddd;
        margin: 0;
        font-size: 12px;
      }
    }
  }
}

.line {
  margin: 0 auto;
  margin-top: 17px;
  width: 1863px;
  height: 1px;
  background: #CAC297;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    width: 15px;
    height: 5px;
    background-image: url('~@/assets/img/gongxuzhongxin/logo-bottom-line.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: left center;
  }

  &::after {
    content: '';
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    width: 15px;
    height: 5px;
    background-image: url('~@/assets/img/gongxuzhongxin/logo-bottom-line.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: right center;
  }
}

/* 中文设计稿：scoped 内覆盖默认 24px */
.page-header.locale-zh-CN {
  .top-navbar .navbar-content {
    .logo-area .site-title {
      font-size: 35px;
    }

    .lang-entry .el-dropdown-link,
    .auth-area .auth-link {
      font-size: 18px;
    }
  }
}
</style>
